import type { CollectionMetaWithStringDisplayTemplate, ExtendApi } from '#layers/data-model/types/constants/schema-overrides';
import type { GlobalCollectionId, GlobalConstraintId } from '#layers/data-model/types/schema/ids';
import type { ResolvedMonospaceCollection, ResolvedMonospaceDataSource } from '#layers/data-model/types/schema/resolved-schema';
import type { MonospaceCollectionMeta, MonospaceSingleConstraintField } from '#layers/data-model/types/schema/workspace-schema';
import type { WorkspaceEntityStore } from '#layers/runtime/public';
import { callOrReturn, isNil, randomUuid } from '#layers/base/public';
import { WORKSPACE_COLLECTIONS } from '#layers/data-model/constants/collections';
import { toDisplayTemplateAst } from '#layers/data-model/utils/display-template';
import { findCollectionPrimitiveFieldByApiName, findCollectionRelationFieldByApiName } from '#layers/data-model/utils/fields/fields';

export function createExtendApi(
	collection: ResolvedMonospaceCollection,
	state: Record<string, Record<string, any>>,
	collectionsByName: Map<string, ResolvedMonospaceCollection>,
	store: WorkspaceEntityStore,
): ExtendApi<any> {
	return {
		addPrimitiveField(field) {
			const collectionState = state['MonospacePrimitiveField'] ??= {};

			collectionState[field.id] = { ...field, isStudioOnly: true };

			if (field.meta) {
				const collectionStateMeta = state['MonospacePrimitiveFieldMeta'] ??= {};
				collectionStateMeta[field.id] = { ...field.meta, fieldId: field.id };
			}

			return field.id;
		},
		addSingleRelationField(fieldName, toCollectionName, options) {
			const fromCollectionId = collection.id;
			const fieldId = options.id;
			const constraintId = randomUuid() as GlobalConstraintId;

			const toCollection = collectionsByName.get(toCollectionName);

			if (!toCollection) {
				console.error(`Could not find collection with name ${toCollectionName}`);
				return;
			}

			const singleRelationFieldState = state['MonospaceSingleRelationField'] ??= {};

			singleRelationFieldState[fieldId] = {
				id: fieldId,
				collectionId: fromCollectionId,
				oppositeCollectionId: toCollection.id,
				apiName: fieldName,
				isList: options.isList ?? false,
				constraintId: options.fields ? constraintId : undefined,
				isStudioOnly: true,
			};

			if (options.meta) {
				const singleRelationFieldMetaState = state['MonospaceSingleRelationFieldMeta'] ??= {};
				singleRelationFieldMetaState[fieldId] = { ...options.meta, fieldId };
			}

			if (options.fields) {
				const singleConstraintState = state['MonospaceSingleConstraint'] ??= {};
				const singleConstraintFieldState = state['MonospaceSingleConstraintField'] ??= {};
				// The store keys layer state from plain mappings, the same as the schema's own `getKey`.
				const getConstraintFieldKey = store.MonospaceSingleConstraintField.getKey as (item: MonospaceSingleConstraintField) => string | undefined;

				singleConstraintState[constraintId] = {
					id: constraintId,
				};

				for (const [order, fromFieldPath, toFieldPath] of Object.entries(options.fields).map((fields, index): [number, string, string] => [index, ...fields as [string, string]])) {
					const fromFieldName = fromFieldPath.split('.')[1]!;
					const toFieldName = toFieldPath.split('.')[1]!;
					const fromField = findCollectionPrimitiveFieldByApiName(collection, fromFieldName);
					const toField = findCollectionPrimitiveFieldByApiName(toCollection, toFieldName);

					if (!fromField) {
						console.error(`Could not find field ${fromFieldName} in collection ${collection.apiName}`);
						continue;
					}

					if (!toField) {
						console.error(`Could not find field ${toFieldName} in collection ${toCollection.apiName}`);
						continue;
					}

					const fieldMapping: MonospaceSingleConstraintField = {
						order,
						constraintId,
						constrainedFieldId: fromField.id,
						referencedFieldId: toField.id,
					};

					singleConstraintFieldState[getConstraintFieldKey(fieldMapping)!] = fieldMapping;
				}
			}

			return fieldId;
		},
	};
}

/**
 * Builds the `MonospaceCollectionMeta` cache entry for a `$meta` override.
 *
 * Overrides may write `displayTemplate` as a template string, but the stored meta is an AST
 * everywhere downstream: field selection reads `content` off it directly, so a raw string resolves
 * to no fields at all and the template's data is never fetched.
 */
export function toCollectionMetaState(
	meta: Partial<CollectionMetaWithStringDisplayTemplate>,
	collection: ResolvedMonospaceCollection,
): Partial<MonospaceCollectionMeta> & { collectionId: GlobalCollectionId } {
	const { displayTemplate, ...rest } = meta;

	return {
		...rest,
		...(!isNil(displayTemplate) && { displayTemplate: toDisplayTemplateAst(displayTemplate, collection) }),
		collectionId: collection.id,
	};
}

interface WorkspaceSchemaOverrideApplicator {
	apply: (sources: ResolvedMonospaceDataSource[]) => void;
}

const applicators = new WeakMap<WorkspaceEntityStore, WorkspaceSchemaOverrideApplicator>();

const OVERRIDE_LAYER_PREFIX = 'schema-overrides:';

/** One resource per entity store; preparation and refresh supply their current schema explicitly. */
export function getWorkspaceSchemaOverrides(store: WorkspaceEntityStore): WorkspaceSchemaOverrideApplicator {
	const existing = applicators.get(store);
	if (existing) return existing;
	const overriddenCollections = new Set<string>();
	const applicator: WorkspaceSchemaOverrideApplicator = {
		apply(sources) {
			const collections = sources.flatMap((source) => source.collections);

			applySchemaOverridesToStore(store, overriddenCollections, new Map(collections.map((collection) => [collection.apiName, collection])));
		},
	};
	applicators.set(store, applicator);
	return applicator;
}

/**
 * Writes the override layer a collection owns. An empty state is how "no overrides" is spelled:
 * the applicator owns one layer per collection for the life of the store and replaces its contents,
 * so a collection that loses its overrides is cleared rather than having its layer removed.
 */
function writeOverrideLayer(store: WorkspaceEntityStore, collectionName: string, state: Record<string, any>) {
	// A collection the previous schema registered can be gone by the next apply; rstore throws on a layer for an unknown collection.
	if (!store.$collections.some((collection) => collection.name === collectionName)) return;

	store.$cache.addLayer({
		id: `${OVERRIDE_LAYER_PREFIX}${collectionName}`,
		collectionName,
		state,
		deletedItems: new Set(),
	});
}

function applySchemaOverridesToStore(store: WorkspaceEntityStore, overriddenCollections: Set<string>, collectionsByName: Map<string, ResolvedMonospaceCollection>) {
	const state: Record<string, Record<string, any>> = {};

	// Clear the previous run's overrides before field lookups, otherwise they
	// (e.g. apiName: '') can cause fields to not be found by their original name.
	for (const collectionName of overriddenCollections) writeOverrideLayer(store, collectionName, {});

	for (const [collectionKey, config] of Object.entries(WORKSPACE_COLLECTIONS)) {
		if (!config?.schema) continue;

		const collection = collectionsByName.get(collectionKey);

		if (!collection) {
			console.warn(`Could not find collection ${collectionKey}`);
			continue;
		}

		const overrides = config.schema;

		const { $extend, $meta, ...fieldOverrides } = callOrReturn(overrides, collection);

		if ($meta) {
			state['MonospaceCollectionMeta'] ??= {};
			state['MonospaceCollectionMeta'][collection.id] = toCollectionMetaState($meta, collection);
		}

		for (const fieldKey in fieldOverrides) {
			const overrides = (fieldOverrides as Record<string, any>)[fieldKey];
			const primitiveField = findCollectionPrimitiveFieldByApiName(collection, fieldKey);
			const relationField = findCollectionRelationFieldByApiName(collection, fieldKey);

			const field: any = primitiveField ?? relationField;

			if (field) {
				const { apiName: _, ...fieldOverrides } = callOrReturn(overrides, field);

				const collectionState = state[field.$collection] ??= {};
				collectionState[field.$getKey()] = { ...fieldOverrides };

				if (fieldOverrides.meta) {
					const collectionStateMeta = state[`${field.$collection}Meta`] ??= {};
					collectionStateMeta[field.$getKey()] = { ...fieldOverrides.meta, fieldId: field.id };
				}
			}
			else {
				console.warn(`Could not find field ${fieldKey} in collection ${collectionKey}. Available fields ${collection.primitiveFields.map((field) => field.apiName).join(', ') || 'none'}`);
			}
		}

		if ($extend) {
			$extend(createExtendApi(collection, state, collectionsByName, store));
		}
	}

	for (const collectionName in state) {
		overriddenCollections.add(collectionName);
		writeOverrideLayer(store, collectionName, state[collectionName]!);
	}
}
